<script setup lang="ts">
import { capitalizeFirstLetter } from "~/common/helpers"
import HomeLayout from "~/layouts/HomeLayout.vue"
import type { Product } from "~/models/Product"
import { useSearchStore } from "~/stores/searchStore"
import DropdownCard from "~/components/DropdownCard.vue"
import { usePagination } from "~/composables/pagination"
import { useCategoriesStore } from "~/stores/categoriesStore"
import { useShopListStore } from "~/stores/shopListStore"
import { SUPPORTED_PAGES_ENUM } from "~/common/breadCrumbs"
import CustomTreeFilter from "~/components/CustomTreeFilter.vue"
import RatingFilter from "~/components/RatingFilter.vue"
import CategoryFilterSkeleton from "~/components/Skeletons/CategoryFilterSkeleton.vue"
import { useHomeSearchFilter } from "~/composables/useHomeSearchFilter"

/**
 *
 *  Props and emits section
 *
 *
 */

definePageMeta({
    layout: false,
})

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const { onQueryChange: onQueryChangeFn } = useHomeSearchFilter()
const shopListStore = useShopListStore()
const categoriesStore = useCategoriesStore()
const store = useShopStore()
const route = useRoute()
const router = useRouter()
const i18n = useI18n()
const searchStore = useSearchStore()
const { selectedProvince } = useProvinceSelection()

useSeoMeta({
    title: 'Marketplace',
    description: 'Explora todas las tiendas y productos de Dapamas. Encuentra las mejores ofertas en un solo lugar.',
    ogTitle: 'Marketplace - Dapamas',
    ogDescription: 'Explora todas las tiendas y productos de Dapamas. Encuentra las mejores ofertas en un solo lugar.',
    ogType: 'website',
})
const {
    paginator: products,
    pages,
    page,
    loading: productsLoading,
    handlePaginate,
    addFilter,
    removeFilter,
} = usePagination<Product>("public/products", "GET", "products")
const {
    data: configs,
    loading: configsLoading,
    get: fetchConfigs,
} = useOFetchCustom("store/configs")
const isFirstRender = ref(true)

const { $bus } = useNuxtApp()
const min = ref<number>(0)
const max = ref<number>(0)

const priceRange = ref<{ min: number; max: number }>({
    min: min.value,
    max: max.value,
})

const mostSoldProducts = computed(() => {
    const msp = configs.value?.data?.mostSoldProduct
    if (!msp) {
        return [] as Product[]
    }
    return msp as Product[]
})

const title = ref(
    shopListStore.currentShop && shopListStore.currentShop.name === "TioDomin"
        ? "Todo lo que tu oficina necesita, con calidad y confianza."
        : "Todos nuestros productos"
)

const subtitle = ref(
    shopListStore.currentShop && shopListStore.currentShop.name === "TioDomin"
        ? ""
        : "con los mejores precios en un solo lugar"
)

const brand = ref()
const offersValue = ref(false)
const moreSellingValue = ref(false)
const soonValue = ref(false)

const offers = computed({
    get() {
        return offersValue.value
    },
    set(newValue) {
        if (newValue === true) {
            moreSellingValue.value = false
            soonValue.value = false
        }
        offersValue.value = newValue
    },
})
const moreSelling = computed({
    get() {
        return moreSellingValue.value
    },
    set(newValue) {
        if (newValue === true) {
            offersValue.value = false
            soonValue.value = false
        }
        moreSellingValue.value = newValue
    },
})
const soon = computed({
    get() {
        return soonValue.value
    },
    set(newValue) {
        if (newValue === true) {
            offersValue.value = false
            moreSellingValue.value = false
        }
        soonValue.value = newValue
    },
})

const lastBreadCrumbName = computed(() => {
    if (offers.value) return "Ahora en Oferta"
    if (soon.value) return "Próximamente"
    if (moreSelling.value) return "Lo más vendido"

    return !categoriesStore.currentCategorySelected ||
        categoriesStore.currentCategorySelected === "Todos"
        ? i18n.t("breadCrumbs.allCategories")
        : capitalizeFirstLetter(categoriesStore.currentCategorySelected)
})

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(() => {
    $bus.$on("update:product-list", (params: any) => {
        // Directly reload products instead of calling onQueryChange
        getProducts()
    })

    $bus.$on("update:only-combo", (value: boolean) => {
        console.log(value, "value-only_combo")
        onOnlyComboChange(value)
    })

    $bus.$on("refresh:store-view", () => {
        fetchConfigs(undefined, {
            onSuccess: ({ data }) => {
                min.value = data.prices.min
                max.value = data.prices.max
                priceRange.value = {
                    min: min.value,
                    max: max.value,
                }
            },
        })
        router.push("/store")
    })

    const query = route.query.query ? String(route.query.query) : undefined
    const category = route.query.category
        ? (route.query.category as string)
        : undefined

    const onlyCombo = route.query.only_combo
        ? (route.query.only_combo as string)
        : undefined

    fetchConfigs()

    if (query) {
        onQueryChange(query)
    }

    if (category) {
        onCategoryChange(category)
    }

    if (onlyCombo) {
        onOnlyComboChange(onlyCombo)
    }

    if (!category && !query && !onlyCombo) {
        getProducts()
    }

    products.value?.data?.forEach((product) => {
        if (product.price && product.price < min.value) {
            min.value = product.price
        }
        if (product.price && product.price > max.value) {
            max.value = product.price
        }
    })
})

watch(
    () => products.value,
    (newValue) => {
        if (isFirstRender.value) {
            inferMinMax()
        }
    }
)

watch(
    brand,
    (newValue) => {
        if (newValue != undefined || newValue != null) {
            addFilter("brand", [...newValue])
        } else {
            removeFilter("brand")
        }
        getProducts()
    },
    {
        immediate: true,
        deep: true,
    }
)

watch(offers, (newValue) => {
    cleanFiltersAndAddFilter(["best_sellers", "soon"], {
        name: "offers",
        value: true,
    })
    getProducts()
})

watch(moreSelling, (newValue) => {
    cleanFiltersAndAddFilter(["offers", "soon"], {
        name: "best_sellers",
        value: true,
    })
    getProducts()
})

watch(soon, (newValue) => {
    cleanFiltersAndAddFilter(["best_sellers", "offers"], {
        name: "soon",
        value: true,
    })
    getProducts()
})

watch(
    () => searchStore.query,
    (newValue) => {
        onQueryChange(newValue)
    }
)

watch(
    () => route.query.category,
    (newValue) => {
        categoriesStore.setCurrentCategorySelected(
            categoriesStore.categories.find((c) => c.label === newValue)
                ?.label || null
        )
        if (newValue) {
            onCategoryChange(newValue as string)
        }
    }
)

/**
 *
 *  Functions section
 *
 *
 */

function onQueryChange(queryObj: any) {
    onQueryChangeFn({
        params: {
            ...route.query,
            query: queryObj?.params?.query || searchStore.query,
            category: queryObj?.params?.category || searchStore.category,
        },
        onSuccess: () => {
            searchStore.setQuery(queryObj?.params?.query || searchStore.query)
            addFilter("query", queryObj?.params?.query || searchStore.query)
            categoriesStore.setCurrentCategorySelected(
                queryObj?.params?.category || searchStore.category
            )
            addFilter(
                "category",
                queryObj?.params?.category || searchStore.category
            )
        },
        onFinish: () => {
            if (selectedProvince.value) {
                addFilter("province", selectedProvince.value)
            }
            getProducts()
        },
    })
}

function onCategoryChange(category: string) {
    onQueryChangeFn({
        params: {
            ...route.query,
            category: category,
        },
        onSuccess: () => {
            categoriesStore.setCurrentCategorySelected(category)
            addFilter("category", category)
        },
        onFinish: () => {
            getProducts()
        },
    })
}

function onOnlyComboChange(value: boolean) {
    onQueryChangeFn({
        params: {
            ...route.query,
            only_combo: value,
        },
        onSuccess: () => {
            addFilter("only_combo", value)
        },
        onFinish: () => {
            getProducts()
        },
    })
}

function onTopButtonsBarChanged(val: Map<string, any>) {
    val.forEach((value, key) => {
        addFilter(key, value)
    })

    getProducts()
}

function inferMinMax() {
    if (products && products.value.data && products.value.data.length > 0) {
        const sorted = [...(products.value.data as Array<Product>)].sort(
            (a, b) => Number(a.price) - Number(b.price)
        )
        let minInner = sorted[0].price ?? 0
        let maxInner = sorted[sorted.length - 1].price ?? 0
        min.value = minInner === maxInner ? 0 : minInner
        max.value = maxInner

        priceRange.value = {
            min: Number(minInner),
            max: Number(maxInner),
        }
        isFirstRender.value = false
    }
}

function onChangeMin(val: any) {
    addFilter("min", val)
    priceRange.value.min = val
    getProducts()
}
function onChangeMax(val: any) {
    addFilter("max", val)
    priceRange.value.max = val
    getProducts()
}
function onChangeRange(val: any) {
    priceRange.value = val
    addFilter("min", priceRange.value.min)
    addFilter("max", priceRange.value.max)
    getProducts()
}

const cleanFiltersAndAddFilter = (
    toRemoveFilters: string[],
    toAddFilter: { name: string; value: any }
) => {
    toRemoveFilters.forEach((filter) => {
        removeFilter(filter)
    })
    addFilter(toAddFilter.name, toAddFilter.value)
}
const onRatingFilterChanged = (val: number) => {
    addFilter("rating", val)
    getProducts()
}

function getProducts() {
    // If province is provided in params, add it as filter, otherwise get it from selectedProvince
    if (selectedProvince.value) {
        addFilter("province", selectedProvince.value)
    }
    handlePaginate()
}
</script>
<template>
    <HomeLayout
        :title="title"
        :subtitle="subtitle"
        :currentPage="SUPPORTED_PAGES_ENUM.Store"
        withoutParentStyles
        :customBanner="
            shopListStore.currentShop?.name === 'TioDomin'
                ? '/public/img/banners/banner-home-tiodomin.png'
                : undefined
        "
        :lastBreadCrumbName="lastBreadCrumbName"
    >
        <div class="tw-grid tw-grid-cols-12 tw-gap-5 max-container">
            <div
                class="tw-col-span-3 tw-hidden lg:tw-flex tw-flex-col tw-gap-5 tw-my-8"
            >
                <DropdownCard :title="$t('categories.title')" opened>
                    <template v-if="categoriesStore.loadingCategories">
                        <CategoryFilterSkeleton :lines="5" height="5" />
                    </template>
                    <CustomTreeFilter
                        v-else
                        :options="categoriesStore.categories"
                        @onSelect="onCategoryChange"
                    />
                </DropdownCard>
                <DropdownCard :title="$t('home.price')" opened>
                    <div
                        class="tw-flex tw-justify-between tw-items-center tw-gap-2 tw-text-customGray-400"
                    >
                        <TextInput
                            @update:model-value="onChangeMin"
                            v-model="priceRange.min"
                            type="number"
                            :min="0"
                            dense
                            debounce="500"
                            class="tw-font-body tw-text-sm price-range-input lg:tw-min-w-[75px]"
                            input-class="tw-text-primary-800"
                        >
                            <template #prepend>
                                <div class="prepend_text_input tw-text-sm">
                                    {{ $t("fields.min") }}
                                </div>
                            </template>
                        </TextInput>
                        <div
                            class="tw-text-lg tw-font-bold tw-text-element_content !tw-flex !tw-items-end tw-h-full"
                        >
                            -
                        </div>
                        <TextInput
                            @update:model-value="onChangeMax"
                            v-model="priceRange.max"
                            type="number"
                            :min="priceRange.min"
                            dense
                            debounce="500"
                            class="tw-font-body tw-text-sm price-range-input lg:tw-min-w-[75px]"
                            input-class="tw-text-primary-800"
                        >
                            <template #prepend>
                                <div class="prepend_text_input tw-text-sm">
                                    {{ $t("fields.max") }}
                                </div>
                            </template>
                        </TextInput>
                    </div>
                    <q-range
                        :model-value="priceRange"
                        @change="onChangeRange"
                        :min="min"
                        :max="max"
                        dense
                        label
                        color="primary"
                        class="tw-p-2"
                    />
                </DropdownCard>
                <DropdownCard :title="$t('home.relevant.title')" opened>
                    <div class="tw-flex tw-flex-col tw-gap-2 relevant-filters">
                        <q-checkbox
                            v-model="offers"
                            :label="$t('home.relevant.offers')"
                        />
                        <q-checkbox
                            v-model="moreSelling"
                            :label="$t('home.relevant.more_selling')"
                        />
                        <q-checkbox
                            v-model="soon"
                            :label="$t('home.relevant.soon')"
                        />
                    </div>
                </DropdownCard>
                <SelectApi
                    multiple
                    use-chips
                    resource="public/brands"
                    v-model:selected="brand"
                    :label="$t('fields.brand')"
                    key-label="name"
                    label-color="primary"
                    key-name="id"
                    borderless
                    hide-dropdown-icon
                    class="brand-select tw-relative"
                    withCustomIcon
                >
                    <template #label>
                        <div
                            class="tw-text-primary tw-font-semibold tw-text-2xl"
                        >
                            {{ $t("fields.brand") }}
                        </div>
                    </template>
                </SelectApi>
                <DropdownCard :title="$t('home.reviews')" opened>
                    <RatingFilter
                        size="20px"
                        @updateFilter="onRatingFilterChanged"
                    />
                </DropdownCard>
            </div>
            <div class="tw-col-span-12 lg:tw-col-span-9">
                <TopButtonsBar
                    class="tw-my-4"
                    :title="lastBreadCrumbName"
                    :view="store.view"
                    :pagination="products"
                    @update:pagination="onTopButtonsBarChanged"
                    :totalProducts="products?.total"
                    :views="store.views"
                />

                <q-separator color="customGray-100" class="tw-mb-4" />

                <ProductsNotFound
                    v-if="!productsLoading && products.data?.length === 0"
                    class="tw-justify-center tw-my-20"
                />

                <div
                    class="tw-grid tw-grid-cols-12 tw-gap-2 md:tw-gap-4"
                    id="products"
                >
                    <ProductCardSkeleton
                        class="tw-col-span-12 sm:tw-col-span-6 md:tw-col-span-6 lg:tw-col-span-6 xl:tw-col-span-3"
                        v-show="productsLoading"
                        v-for="index in 8"
                        :key="index"
                    />

                    <component
                        v-show="!productsLoading"
                        class="tw-mt-1 tw-justify-self-center tw-h-full md:tw-mt-0 tw-w-full tw-col-span-6 xl:tw-col-span-3 fadeIn"
                        :is="store.getComponent"
                        v-for="product in products.data"
                        :product="product"
                        :key="product.id"
                        isHome
                    />
                </div>

                <div class="tw-flex tw-justify-center tw-my-5">
                    <q-pagination
                        :max="pages"
                        v-model="page"
                        flat
                        direction-links
                        max-pages="6"
                        boundary-numbers
                        @update:model-value="getProducts"
                    />
                </div>
            </div>
        </div>

        <template #most_sold v-if="mostSoldProducts.length > 0">
            <MoreProducts
                :title="$t('messages.most_selled_product')"
                :subtitle="$t('messages.most_selled_product_subtitle')"
                :products="mostSoldProducts"
            />
        </template>
    </HomeLayout>
</template>
<style>
.price-range-input .q-field__native {
    @apply !tw-py-0 tw-px-1 tw-my-auto;
}

.brand-select {
    @apply tw-max-h-[60px] tw-border tw-rounded-[3px] tw-border-customGray-100 tw-px-8;
}
</style>
